import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sidetopp (Portal 2026): tittel 24/30 i display-skrift, maks én linje kontekst (14/20), handlinger til høyre.
 * Primærhandlingen legges først blant `children`; sidene holder seg til 1–3 handlinger.
 */
export function PageHeader({ title, lead, breadcrumbs, children, className, titleTestId = 'page-title', leadTestId = 'page-subtitle' }) {
  return (
    <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)} data-testid="page-header">
      <div className="min-w-0">
        {breadcrumbs && (
          <nav aria-label="Brødsmuler" className="mb-2 flex items-center gap-1 text-[13px] leading-[18px] text-[var(--hr-muted)]">
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={b.label}>
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />}
                {b.to ? (
                  <Link to={b.to} className="rounded-sm text-[var(--hr-muted)] transition-colors duration-150 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={b.testId}>
                    {b.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="truncate text-[var(--hr-text)]">{b.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        <h1 className="text-[24px] font-semibold leading-[30px] tracking-[-0.015em] text-[var(--hr-heading)]" data-testid={titleTestId}>{title}</h1>
        {lead && <p className="mt-1 max-w-[90ch] text-sm leading-5 text-[var(--hr-muted)]" data-testid={leadTestId}>{lead}</p>}
      </div>
      {children && <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pt-0.5">{children}</div>}
    </div>
  );
}

/** Seksjonstittel (H2 16/24) utenfor paneler. */
export function SectionTitle({ children, className, as: Tag = 'h2' }) {
  return <Tag className={cn('text-[16px] font-semibold leading-6 text-[var(--hr-heading)]', className)}>{children}</Tag>;
}

/** Liten overlinje (12 px, versaler) – kun som gruppering, aldri som eneste etikett. */
export function Eyebrow({ children, className, tone = 'muted' }) {
  return <div className={cn('text-[12px] font-semibold uppercase leading-4 tracking-[0.08em]', tone === 'accent' ? 'text-[var(--hr-accent-text)]' : tone === 'amber' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]', className)}>{children}</div>;
}

/** Nøkkel/verdi-liste: etikett 13 px, verdi 14–15 px. */
export function DefList({ items, className, cols = 2 }) {
  return (
    <dl className={cn('grid gap-x-8 gap-y-4 text-sm', cols === 1 ? '' : cols === 3 ? 'sm:grid-cols-3' : cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2', className)}>
      {items.map((it) => (
        <div key={it.label} className="flex min-w-0 flex-col gap-1">
          <dt className="text-[13px] leading-[18px] text-[var(--hr-muted)]">{it.label}</dt>
          <dd className={cn('hr-tabular break-words text-[15px] font-medium leading-5 text-[var(--hr-text)]', it.className)} data-testid={it.testId}>
            {it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
